import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Building, DollarSign, TrendingUp, Users, ChevronRight, ChevronLeft,
  AlertTriangle, CheckCircle, RefreshCw, ExternalLink, BarChart3,
  Layers, Target, Zap, MapPin, Calendar, Shield, Info,
  ArrowRight, GraduationCap, Briefcase, Globe, Award, X, Maximize2,
  ChevronDown, ChevronUp, BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISTRICT_ID = 'demo-wcpss-district';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

function CaryHighSlide({ caryDetail }) {
  if (!caryDetail) return <div className="text-center py-12 text-[#C6D4E8]">Loading Cary High data...</div>;
  return (
    <div className="space-y-4 mt-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Object.entries(caryDetail).slice(0, 4).map(([k, v]) => (
          <div key={k} className="rounded-xl p-4 text-center" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.18)' }}>
            <p className="text-xl font-bold text-white">{typeof v === 'number' ? v.toLocaleString() : v}</p>
            <p className="text-[10px] text-[#C6D4E8] mt-1 capitalize">{k.replace(/_/g, ' ')}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


const fmt = (n) => n ? `$${(n / 1_000_000).toFixed(1)}M` : '-';
const fmtFull = (n) => n ? `$${Number(n).toLocaleString()}` : '-';
const pct = (n) => `${n ?? 0}%`;

// ── Data Type Badge ────────────────────────────────────────────────────────
function DataBadge({ type, label, size = 'sm' }) {
  const config = {
    official_public_data:       { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/25', dot: 'bg-emerald-400', icon: Shield },
    futurepath_modeled_scenario: { color: 'text-violet-400',  bg: 'bg-violet-500/10 border-violet-500/25',  dot: 'bg-violet-400',  icon: Zap },
    futurepath_live_data:        { color: 'text-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/25',      dot: 'bg-cyan-400',    icon: BarChart3 },
    synthetic_demo_data:         { color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/25',    dot: 'bg-amber-400',   icon: Info },
  };
  const c = config[type] || config.official_public_data;
  const Icon = c.icon;
  const textSize = size === 'xs' ? 'text-[9.5px]' : 'text-[10.5px]';
  const px = size === 'xs' ? 'px-1.5 py-0.5' : 'px-2 py-0.5';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-semibold ${textSize} ${px} ${c.bg} ${c.color}`}>
      <Icon className="h-2.5 w-2.5 shrink-0" />
      {label}
    </span>
  );
}

// ── Money Display ──────────────────────────────────────────────────────────
function MoneyCard({ label, amount, sublabel, type = 'official_public_data', color = 'cyan', large = false }) {
  const colors = {
    cyan:   { val: 'text-cyan-300',   bg: 'bg-cyan-500/[0.06]',   border: 'border-cyan-500/20' },
    emerald:{ val: 'text-emerald-300',bg: 'bg-emerald-500/[0.06]',border: 'border-emerald-500/20' },
    violet: { val: 'text-violet-300', bg: 'bg-violet-500/[0.06]', border: 'border-violet-500/20' },
    amber:  { val: 'text-amber-300',  bg: 'bg-amber-500/[0.06]',  border: 'border-amber-500/20' },
  };
  const c = colors[color] || colors.cyan;
  return (
    <div className={`rounded-2xl border ${c.bg} ${c.border} p-5`}>
      <p className={`${large ? 'text-4xl' : 'text-3xl'} font-black tabular-nums ${c.val}`}>{fmt(amount)}</p>
      <p className="mt-1 text-sm font-semibold text-white">{label}</p>
      {sublabel && <p className="mt-0.5 text-[11px] text-slate-300">{sublabel}</p>}
      <div className="mt-2">
        <DataBadge type={type} label={type === 'official_public_data' ? 'Verified Public Data' : 'FuturePath Modeled'} size="xs" />
      </div>
    </div>
  );
}

// ── Tab Button ─────────────────────────────────────────────────────────────
function Tab({ label, icon: Icon, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-semibold transition-all whitespace-nowrap ${
        active
          ? 'bg-cyan-400/[0.12] text-cyan-300 border border-cyan-400/25'
          : 'text-slate-300 hover:text-white hover:bg-white/[0.04] border border-transparent'
      }`}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {label}
    </button>
  );
}

// ── Section Card ──────────────────────────────────────────────────────────
function Section({ title, subtitle, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-white/[0.07] bg-[#0b1b30] p-6 ${className}`}>
      {title && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11.5px] text-slate-300">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function BondIntelligencePage() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [roi, setRoi] = useState(null);
  const [caryDetail, setCaryDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [presentationMode, setPresentationMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showIntro, setShowIntro] = useState(
    () => localStorage.getItem('bond_intro_dismissed') !== '1'
  );

  const dismissIntro = () => {
    localStorage.setItem('bond_intro_dismissed', '1');
    setShowIntro(false);
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/bond/${DISTRICT_ID}/overview`, auth()),
      axios.get(`${API}/bond/${DISTRICT_ID}/projects`, auth()),
      axios.get(`${API}/bond/${DISTRICT_ID}/roi`, auth()),
      axios.get(`${API}/bond/${DISTRICT_ID}/project/wcpss-project-cary-high-fy2029`, auth()).catch(() => ({ data: null })),
    ])
      .then(([ov, pr, ri, cary]) => {
        setOverview(ov.data);
        setProjects(pr.data.projects || []);
        setRoi(ri.data);
        setCaryDetail(cary.data);
      })
      .catch(e => setError(e.response?.data?.detail || 'Could not load bond data'))
      .finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/bond/${DISTRICT_ID}/refresh`, {}, auth());
      setRefreshResult(r.data);
      // Reload overview
      const ov = await axios.get(`${API}/bond/${DISTRICT_ID}/overview`, auth());
      setOverview(ov.data);
    } catch (e) {
      setRefreshResult({ message: 'Refresh failed. Try again later.' });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    </AppLayout>
  );

  if (error) return (
    <AppLayout>
      <div className="rounded-xl border border-rose-400/25 bg-rose-400/[0.06] p-6 text-rose-300 text-sm">
        <AlertTriangle className="mb-2 h-5 w-5" />{error}
      </div>
    </AppLayout>
  );

  if (presentationMode) {
    return (
      <PresentationMode
        overview={overview}
        projects={projects}
        roi={roi}
        caryDetail={caryDetail}
        onExit={() => setPresentationMode(false)}
      />
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="bond-intelligence-page">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl border border-cyan-300/20 bg-[#0a1729] px-8 py-10">
          <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.15), transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.10), transparent 70%)' }} />

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30
                bg-cyan-400/[0.07] px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-cyan-200">
                <Building className="h-3 w-3" /> Community Impact Intelligence
              </span>
              <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
                Wake County Education<br />Capital Investment
              </h1>
              <p className="mt-2 text-sm text-slate-300 max-w-xl">
                From capital planning to human-capital planning. Every bond dollar connected to students, pathways, and community outcomes.
              </p>

              {/* Freshness badge */}
              {overview?.freshness && (
                <div className="mt-3 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
                    overview.freshness.color === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400' :
                    overview.freshness.color === 'amber'   ? 'bg-amber-500/10 border-amber-500/25 text-amber-400' :
                                                              'bg-rose-500/10 border-rose-500/25 text-rose-400'
                  }`}>
                    <Shield className="h-2.5 w-2.5" />
                    {overview.freshness.label}
                  </span>
                  <DataBadge type="official_public_data" label="WCPSS Official Source" size="xs" />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-bond-data"
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03]
                  px-4 py-2.5 text-[12px] font-medium text-slate-300 hover:bg-white/[0.06]
                  disabled:opacity-50 transition-all">
                <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Verifying…' : 'Refresh Public Data'}
              </button>
              <button
                onClick={() => setPresentationMode(true)}
                data-testid="enter-presentation-mode"
                className="flex items-center gap-2 rounded-xl border border-cyan-400/30
                  bg-cyan-400/[0.08] px-4 py-2.5 text-[12px] font-semibold text-cyan-300
                  hover:bg-cyan-400/[0.13] transition-all">
                <Maximize2 className="h-3.5 w-3.5" />
                Presentation Mode
              </button>
            </div>
          </div>

          {/* Refresh result */}
          <AnimatePresence>
            {refreshResult && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-4 flex items-start gap-3 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.06] p-3">
                <CheckCircle className="h-4 w-4 text-cyan-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[12px] text-cyan-200 font-medium">{refreshResult.message}</p>
                  {refreshResult.note && <p className="text-[11px] text-slate-300 mt-0.5">{refreshResult.note}</p>}
                  {refreshResult.changes?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] font-semibold text-amber-400">DATA UPDATED:</p>
                      {refreshResult.changes.map((c, i) => (
                        <p key={i} className="text-[11px] text-slate-300">
                          {c.field}: {fmtFull(c.previous)} → {fmtFull(c.current)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setRefreshResult(null)} className="ml-auto text-slate-300 hover:text-slate-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Community Impact Intro Frame ───────────────────────────────── */}
        {showIntro && <BondIntroFrame onDismiss={dismissIntro} />}

        {/* ── Tabs ───────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2 overflow-x-auto" data-testid="bond-tabs">
          {[
            { id: 'overview',     label: 'Investment Overview', icon: DollarSign },
            { id: 'capital_map',  label: 'Capital Map',         icon: MapPin },
            { id: 'human_capital',label: 'Human Capital ROI',   icon: Users },
            { id: 'scenario',     label: 'Scenario Lab',        icon: TrendingUp },
            { id: 'comparison',   label: 'With vs Without FP',  icon: BarChart3 },
          ].map(t => (
            <Tab key={t.id} label={t.label} icon={t.icon}
              active={tab === t.id} onClick={() => setTab(t.id)} />
          ))}
        </div>

        {/* ── Tab Content ────────────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          <motion.div key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}>

            {tab === 'overview'     && <OverviewTab overview={overview} projects={projects} />}
            {tab === 'capital_map'  && <CapitalMapTab projects={projects} districtId={DISTRICT_ID} selectedProject={selectedProject} setSelectedProject={setSelectedProject} />}
            {tab === 'human_capital'&& <HumanCapitalTab roi={roi} projects={projects} />}
            {tab === 'scenario'     && <ScenarioTab districtId={DISTRICT_ID} overview={overview} />}
            {tab === 'comparison'   && <ComparisonTab overview={overview} projects={projects} roi={roi} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ overview: ov, projects }) {
  if (!ov) return null;
  const fyGroups = {};
  for (const p of projects) {
    const k = `FY${p.fiscal_year}`;
    fyGroups[k] = (fyGroups[k] || 0) + 1;
  }

  return (
    <div className="space-y-6">
      {/* Money cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <MoneyCard label="General Obligation Bond" amount={ov.go_bond_amount}
          sublabel="Voter referendum, November 3, 2026" color="cyan" large />
        <MoneyCard label="Pay-As-You-Go Cash" amount={ov.paygo_amount}
          sublabel="No additional debt required" color="emerald" />
        <MoneyCard label="Combined FY28-FY29 Program" amount={ov.combined_total}
          sublabel="Total education capital investment" color="violet" large />
      </div>

      {/* Visual funding breakdown */}
      <Section title="Funding Structure" subtitle="How the $832.3M capital program is funded">
        <div className="space-y-3">
          {[
            { label: '$680M General Obligation Bond', pct: (680/832.3*100), color: '#22d3ee' },
            { label: '$152.3M Pay-As-You-Go Cash',    pct: (152.3/832.3*100), color: '#34d399' },
          ].map(item => (
            <div key={item.label}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[12px] text-slate-300">{item.label}</p>
                <p className="text-[11px] text-slate-300">{item.pct.toFixed(1)}%</p>
              </div>
              <div className="h-3 w-full rounded-full bg-white/[0.04] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${item.pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  className="h-3 rounded-full"
                  style={{ background: item.color }} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-white/[0.03] border border-white/[0.06] px-4 py-3">
          <span className="text-2xl font-black text-white">=</span>
          <div className="ml-2">
            <p className="text-base font-bold text-cyan-300">$832,330,990 Education Capital Program</p>
            <p className="text-[11px] text-slate-300">Combined FY2028 + FY2029 investment</p>
          </div>
          <div className="ml-auto">
            <DataBadge type="official_public_data" label="Verified Public Data" />
          </div>
        </div>
      </Section>

      {/* Key facts */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Referendum Date',         value: 'Nov 3, 2026',  icon: Calendar, color: 'text-cyan-400' },
          { label: 'New Schools Planned',     value: '8 schools',     icon: Building,  color: 'text-emerald-400' },
          { label: 'Existing Facilities',     value: '17 addressed',  icon: Layers,    color: 'text-violet-400' },
          { label: 'Program Duration',        value: '7 years',       icon: TrendingUp,color: 'text-amber-400' },
        ].map(f => (
          <div key={f.label} className="rounded-xl border border-white/[0.07] bg-[#0b1b30] p-4 flex items-start gap-3">
            <f.icon className={`h-5 w-5 ${f.color} shrink-0 mt-0.5`} />
            <div>
              <p className={`text-xl font-bold ${f.color}`}>{f.value}</p>
              <p className="text-[11.5px] text-slate-300 mt-0.5">{f.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Source note */}
      <div className="rounded-xl border border-white/[0.05] bg-white/[0.015] px-5 py-4">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-[12px] font-semibold text-emerald-300">Official Data Source</p>
            <p className="text-[11px] text-slate-300 mt-0.5">
              {ov.description}
            </p>
            <p className="text-[10.5px] text-slate-300 mt-1">
              Source: {ov.source} · {ov.source_url} · Last verified: {ov.verified_at?.slice(0,10)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Capital Map Tab ──────────────────────────────────────────────────────────
function CapitalMapTab({ projects, districtId, selectedProject, setSelectedProject }) {
  const [projectDetail, setProjectDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const handleProjectClick = async (p) => {
    if (selectedProject?.id === p.id) {
      setSelectedProject(null);
      setProjectDetail(null);
      return;
    }
    setSelectedProject(p);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/bond/${districtId}/project/${p.id}`, auth());
      setProjectDetail(r.data);
    } catch {
      setProjectDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const byYear = {};
  for (const p of projects) {
    const k = p.fiscal_year;
    byYear[k] = byYear[k] || [];
    byYear[k].push(p);
  }

  return (
    <div className="space-y-6">
      {Object.keys(byYear).sort().map(yr => (
        <Section key={yr} title={`FY${yr} Capital Projects`}
          subtitle={`${byYear[yr].length} project${byYear[yr].length !== 1 ? 's' : ''}`}>
          <div className="space-y-2">
            {byYear[yr].map(p => (
              <div key={p.id}>
                <button
                  onClick={() => handleProjectClick(p)}
                  data-testid={`project-card-${p.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-all hover:brightness-105 ${
                    selectedProject?.id === p.id
                      ? 'border-cyan-400/40 bg-cyan-400/[0.06]'
                      : 'border-white/[0.07] bg-white/[0.015] hover:border-cyan-400/20'
                  }`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                      p.project_type === 'new_school'
                        ? 'border-emerald-500/30 bg-emerald-500/10'
                        : 'border-violet-500/30 bg-violet-500/10'
                    }`}>
                      {p.project_type === 'new_school'
                        ? <Building className="h-4 w-4 text-emerald-400" />
                        : <Layers className="h-4 w-4 text-violet-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white">{p.school_name}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          p.project_type === 'new_school'
                            ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                            : 'bg-violet-500/15 text-violet-400 border border-violet-500/25'
                        }`}>
                          {p.project_type === 'new_school' ? 'New School' : 'Renovation'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {p.city && <span className="flex items-center gap-1 text-[11px] text-slate-300"><MapPin className="h-2.5 w-2.5" />{p.city}</span>}
                        {p.bond_allocation && <span className="text-[11px] text-cyan-300 font-semibold">{fmt(p.bond_allocation)}</span>}
                        {p.programs_supported?.length > 0 && (
                          <span className="text-[10.5px] text-slate-300">{p.programs_supported.slice(0,2).join(' · ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DataBadge type="official_public_data" label="WCPSS Data" size="xs" />
                      <ChevronRight className={`h-4 w-4 transition-transform ${selectedProject?.id === p.id ? 'rotate-90 text-cyan-400' : 'text-slate-300'}`} />
                    </div>
                  </div>
                </button>

                {/* Drilldown panel */}
                <AnimatePresence>
                  {selectedProject?.id === p.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden">
                      <ProjectDrilldown
                        project={p}
                        detail={projectDetail}
                        loading={detailLoading}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
}

function ProjectDrilldown({ project: p, detail, loading }) {
  return (
    <div className="mt-2 mb-3 ml-11 rounded-xl border border-cyan-400/15 bg-[#030e18] p-5 space-y-5">
      {/* Public Capital Data */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-3.5 w-3.5 text-emerald-400" />
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400">Public Capital Data</p>
          <DataBadge type="official_public_data" label="Official WCPSS Data" size="xs" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Bond Allocation', value: p.bond_allocation ? fmt(p.bond_allocation) : 'TBD' },
            { label: 'Project Type', value: p.project_type === 'new_school' ? 'New Construction' : 'Renovation' },
            { label: 'Fiscal Year', value: `FY${p.fiscal_year}` },
            { label: 'Location', value: p.city || '-' },
          ].map(item => (
            <div key={item.label} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
              <p className="text-[10.5px] text-slate-300">{item.label}</p>
              <p className="text-sm font-semibold text-white mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>
        {p.description && (
          <p className="mt-3 text-[11.5px] text-slate-300 leading-relaxed">{p.description}</p>
        )}
        {p.current_capacity && p.projected_capacity && (
          <div className="mt-3 flex items-center gap-4 rounded-lg bg-white/[0.02] border border-white/[0.05] px-4 py-2.5">
            <div className="text-center">
              <p className="text-lg font-bold text-white">{p.current_capacity.toLocaleString()}</p>
              <p className="text-[10.5px] text-slate-300">Current Capacity</p>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300" />
            <div className="text-center">
              <p className="text-lg font-bold text-cyan-300">{p.projected_capacity.toLocaleString()}</p>
              <p className="text-[10.5px] text-slate-300">Projected Capacity</p>
            </div>
            <div className="ml-4 text-center">
              <p className="text-lg font-bold text-emerald-300">+{(p.projected_capacity - p.current_capacity).toLocaleString()}</p>
              <p className="text-[10.5px] text-slate-300">New Seats</p>
            </div>
          </div>
        )}
      </div>

      {/* FuturePath Human Capital Intelligence */}
      {loading ? (
        <div className="flex items-center gap-2 py-3 text-[11.5px] text-slate-300">
          <div className="h-4 w-4 animate-spin rounded-full border border-cyan-400 border-t-transparent" />
          Loading FuturePath student intelligence…
        </div>
      ) : detail ? (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-3.5 w-3.5 text-violet-400" />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-400">FuturePath Student Intelligence</p>
            <DataBadge type="futurepath_live_data" label="FuturePath Live Data" size="xs" />
          </div>

          {detail.student_intelligence?.status === 'available' ? (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  { label: 'Students Connected', value: detail.student_intelligence.students, color: 'text-cyan-300' },
                  { label: 'Avg CDP Completion', value: `${detail.student_intelligence.avg_cdp_completion}%`, color: 'text-violet-300' },
                  { label: 'Assessments Done', value: detail.student_intelligence.assessment_done_count, color: 'text-emerald-300' },
                ].map(item => (
                  <div key={item.label} className="rounded-lg bg-violet-500/[0.04] border border-violet-500/15 p-3 text-center">
                    <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                    <p className="text-[10.5px] text-slate-300 mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>
              {detail.student_intelligence.top_career_interests?.length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-300 mb-2">Top career interests at this school:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.student_intelligence.top_career_interests.slice(0, 6).map(c => (
                      <span key={c.name} className="text-[11px] px-2.5 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300">
                        {c.name} ({c.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[11.5px] text-slate-300 italic">No FuturePath student data currently connected to this school.</p>
          )}

          {/* Capital Chain */}
          <div className="mt-4 pt-4 border-t border-white/[0.06]">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-400">Capital → Human Capital Chain</p>
              <DataBadge type="futurepath_modeled_scenario" label="FuturePath Modeled Scenario" size="xs" />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              {[
                { label: 'Facility Investment', value: p.bond_allocation ? fmt(p.bond_allocation) : 'TBD', color: 'text-cyan-300' },
                { label: '→', color: 'text-slate-300' },
                { label: 'Students Served', value: detail.student_intelligence?.students || '-', color: 'text-violet-300' },
                { label: '→', color: 'text-slate-300' },
                { label: 'Learning Capacity', value: p.programs_supported?.join(', ') || '-', color: 'text-emerald-300' },
                { label: '→', color: 'text-slate-300' },
                { label: 'Career Outcomes', value: 'See Scenario Lab', color: 'text-amber-300' },
              ].map((item, i) => (
                item.label === '→'
                  ? <ArrowRight key={i} className="h-3 w-3 text-slate-300" />
                  : (
                    <div key={i} className="rounded-lg bg-white/[0.03] border border-white/[0.06] px-2.5 py-1.5 text-center">
                      {item.value && <p className={`font-semibold ${item.color}`}>{item.value}</p>}
                      <p className="text-slate-300">{item.label}</p>
                    </div>
                  )
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Human Capital ROI Tab ────────────────────────────────────────────────────
function HumanCapitalTab({ roi, projects }) {
  if (!roi) return null;
  return (
    <div className="space-y-6">
      {/* Physical ROI */}
      <Section title="Physical Return" subtitle="What the bond builds, official data">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: 'New Schools Built',           value: roi.physical_roi?.new_schools_built || 0,         color: 'text-emerald-400' },
            { label: 'Schools Renovated/Replaced',  value: roi.physical_roi?.schools_renovated_or_replaced || 0, color: 'text-cyan-400' },
            { label: 'Total Capital Projects',      value: roi.physical_roi?.total_projects || 0,            color: 'text-violet-400' },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-5 text-center">
              <p className={`text-4xl font-black ${item.color}`}>{item.value}</p>
              <p className="mt-1 text-[12px] text-slate-300">{item.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <DataBadge type="official_public_data" label="Official WCPSS Data" />
        </div>
      </Section>

      {/* Student ROI */}
      <Section title="Student Return" subtitle="Students connected to this investment">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.04] p-5">
            <p className="text-4xl font-black text-cyan-300">{(roi.student_roi?.students_in_district || 0).toLocaleString()}</p>
            <p className="mt-1 text-[12px] text-slate-300">Students in FuturePath district database</p>
          </div>
          {roi.student_roi?.capacity_created > 0 && (
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.04] p-5">
              <p className="text-4xl font-black text-emerald-300">{roi.student_roi.capacity_created.toLocaleString()}</p>
              <p className="mt-1 text-[12px] text-slate-300">Net new seats created across all projects</p>
            </div>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <DataBadge type="official_public_data" label="WCPSS Official Data" />
        </div>
      </Section>

      {/* Human Capital ROI, CLEARLY MODELED */}
      <Section>
        <div className="flex items-start gap-3 mb-4">
          <Zap className="h-5 w-5 text-violet-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-white">Human Capital Return</h3>
              <DataBadge type="futurepath_modeled_scenario" label="FuturePath Modeled Scenario" />
            </div>
            <p className="mt-0.5 text-[11.5px] text-violet-300/70">
              {roi.human_capital_roi?.description}
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-5">
            <p className="text-[11px] text-violet-400 font-semibold uppercase tracking-wide mb-1">Modeled Annual Cohort</p>
            <p className="text-3xl font-black text-violet-300">{(roi.human_capital_roi?.cohort_assumption || 0).toLocaleString()}</p>
            <p className="text-[11px] text-slate-300 mt-1">students/year connected to facility investments</p>
          </div>
          <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-5">
            <p className="text-[11px] text-violet-400 font-semibold uppercase tracking-wide mb-1">Modeled 10-Year Earnings Impact</p>
            <p className="text-3xl font-black text-violet-300">
              ${(roi.human_capital_roi?.['10yr_modeled_earnings_impact'] || 0).toLocaleString()}
            </p>
            <p className="text-[11px] text-slate-300 mt-1">scenario estimate, see assumptions below</p>
          </div>
        </div>

        {/* Assumptions box */}
        <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
          <p className="text-[11px] font-semibold text-amber-400 mb-2">Model Assumptions (FuturePath Estimate)</p>
          <ul className="space-y-1">
            {(roi.human_capital_roi?.assumptions || []).map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-slate-300">
                <span className="text-amber-400 shrink-0">•</span> {a}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Return per $1 invested */}
      <Section title="Return per $1 Invested" subtitle="Multiple measures, not a single dollar ROI">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: 'Students per $1M invested', value: roi.return_per_million?.students_per_million?.toLocaleString(), unit: 'students/$1M' },
            { label: 'Projects per $100M', value: roi.return_per_million?.projects_per_100m, unit: 'projects/$100M' },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/[0.07] bg-white/[0.015] p-4">
              <p className="text-2xl font-black text-violet-300">{item.value}</p>
              <p className="text-[11px] text-slate-300 mt-0.5">{item.label}</p>
              <p className="text-[10px] text-slate-300">{item.unit}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <DataBadge type="futurepath_modeled_scenario" label="FuturePath Modeled Scenario" />
        </div>
      </Section>
    </div>
  );
}

// ─── Scenario Lab Tab ─────────────────────────────────────────────────────────
function ScenarioTab({ districtId, overview }) {
  const [sliders, setSliders] = useState({
    career_alignment_lift:          0,
    cte_participation_lift:         0,
    wbl_lift:                       0,
    credential_lift:                0,
    postsecondary_persistence_lift: 0,
    local_retention_lift:           0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bond/${districtId}/scenario`, {
        ...auth(),
        params: sliders,
      });
      setResult(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [sliders, districtId]);

  useEffect(() => { run(); }, [run]);

  const LEVERS = [
    { key: 'cte_participation_lift',         label: 'CTE Participation',         baseline: 38, unit: '%' },
    { key: 'wbl_lift',                        label: 'Work-Based Learning',        baseline: 22, unit: '%' },
    { key: 'credential_lift',                 label: 'Credential Attainment',      baseline: 31, unit: '%' },
    { key: 'postsecondary_persistence_lift',  label: 'Postsecondary Persistence',  baseline: 68, unit: '%' },
    { key: 'local_retention_lift',            label: 'Local Talent Retention',     baseline: 42, unit: '%' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4">
        <Zap className="h-4 w-4 text-violet-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[12px] font-semibold text-violet-300">FuturePath Scenario Lab</p>
          <p className="text-[11px] text-slate-300 mt-0.5">
            Adjust assumptions to model how education outcome improvements interact with the $832.3M capital investment.
            All outputs are <strong className="text-violet-300">FuturePath Modeled Scenarios</strong>, not predictions or guarantees.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sliders */}
        <Section title="Adjust Assumptions" subtitle="Slide to model improved outcomes">
          <div className="space-y-5">
            {LEVERS.map(lv => (
              <div key={lv.key}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] text-slate-300">{lv.label}</span>
                  <span className="text-[12px] font-semibold text-cyan-300">
                    Baseline {lv.baseline}{lv.unit} + <strong>{sliders[lv.key]}%</strong> lift
                  </span>
                </div>
                <input
                  type="range" min="0" max="50" step="5"
                  value={sliders[lv.key]}
                  onChange={e => setSliders(prev => ({ ...prev, [lv.key]: Number(e.target.value) }))}
                  className="w-full h-1.5 rounded-full accent-cyan-400 cursor-pointer bg-white/[0.08]"
                  data-testid={`slider-${lv.key}`}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-slate-300">Baseline</span>
                  <span className="text-[10px] text-slate-300">+50%</span>
                </div>
              </div>
            ))}
            <button onClick={run} disabled={loading}
              className="w-full rounded-xl border border-cyan-400/30 bg-cyan-400/[0.08] py-2.5
                text-[12px] font-semibold text-cyan-300 hover:bg-cyan-400/[0.13] transition-all
                disabled:opacity-50">
              {loading ? 'Calculating…' : 'Recalculate Scenario'}
            </button>
          </div>
        </Section>

        {/* Results */}
        <div className="space-y-4">
          {result && (
            <>
              <div className="flex items-center gap-2">
                <DataBadge type="futurepath_modeled_scenario" label="FuturePath Modeled Scenario" />
                <span className="text-[11px] text-slate-300">Based on {(result.modeled_outcomes?.students_in_cte_programs || 0).toLocaleString()} + scenarios</span>
              </div>

              {[
                { label: 'Students in CTE Programs',        value: result.modeled_outcomes?.students_in_cte_programs, color: 'text-cyan-300',   icon: BookOpen },
                { label: 'Students Earning Credentials',    value: result.modeled_outcomes?.students_earning_credentials, color: 'text-emerald-300', icon: Award },
                { label: 'Persisting Postsecondary',        value: result.modeled_outcomes?.students_persisting_postsecondary, color: 'text-violet-300', icon: GraduationCap },
                { label: 'Retained Locally',                value: result.modeled_outcomes?.students_retained_locally, color: 'text-amber-300',  icon: MapPin },
              ].map(item => (
                <div key={item.label}
                  className="flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.015] px-5 py-4">
                  <div className="flex items-center gap-3">
                    <item.icon className={`h-4 w-4 ${item.color}`} />
                    <p className="text-[12px] text-slate-300">{item.label}</p>
                  </div>
                  <p className={`text-xl font-black ${item.color}`}>
                    {(item.value || 0).toLocaleString()}
                  </p>
                </div>
              ))}

              <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.05] p-4">
                <p className="text-[10.5px] font-semibold text-violet-400 mb-1">Modeled 10-Year Earnings Impact</p>
                <p className="text-2xl font-black text-violet-300">
                  ${(result.modeled_outcomes?.['10yr_modeled_earnings_impact'] || 0).toLocaleString()}
                </p>
                <p className="text-[10.5px] text-slate-300 mt-1">{result.disclaimer}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.05] bg-white/[0.01] px-5 py-4 text-[11px] text-slate-300">
        <strong className="text-slate-300">How to read this:</strong> Capital investment (${fmt(overview?.combined_total)}) funds facility capacity. 
        The sliders model what happens when education outcomes improve in schools served by the investment.
        These calculations are illustrative scenarios, they do not imply the bond causes these outcomes.
      </div>
    </div>
  );
}

// ─── Comparison Tab ───────────────────────────────────────────────────────────
function ComparisonTab({ overview: ov, projects, roi }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Without FuturePath */}
        <Section>
          <div className="flex items-start gap-3 mb-5">
            <div className="w-8 h-8 rounded-full bg-slate-700/60 flex items-center justify-center shrink-0">
              <Building className="h-4 w-4 text-slate-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-300">Capital Investment Without FuturePath</p>
              <p className="text-[11px] text-slate-300 mt-0.5">What district leadership can see today</p>
            </div>
          </div>
          <p className="text-[12px] text-slate-300 font-medium mb-3">We know:</p>
          <ul className="space-y-2">
            {[
              'What we are building',
              'Where we are building it',
              'Construction cost',
              'Facility capacity',
              'Project schedule',
              'Referendum vote outcome',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-[12px] text-slate-300">
                <CheckCircle className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Section>

        {/* With FuturePath */}
        <Section>
          <div className="flex items-start gap-3 mb-5">
            <div className="w-8 h-8 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0">
              <Zap className="h-4 w-4 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Capital Investment + FuturePath Intelligence</p>
              <p className="text-[11px] text-slate-300 mt-0.5">What we can additionally understand</p>
            </div>
          </div>
          <p className="text-[12px] text-cyan-400 font-medium mb-3">We can additionally understand:</p>
          <ul className="space-y-2">
            {[
              'Who the investment serves, live student data',
              'Student career interests and pathway demand',
              'Regional workforce needs vs student supply',
              'Employer pipeline by CTE pathway',
              'Postsecondary transition rates',
              'Potential human-capital impact over time',
              'Opportunity gaps by school and geography',
              'Long-term community economic scenarios',
            ].map(item => (
              <li key={item} className="flex items-center gap-2 text-[12px] text-white">
                <CheckCircle className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Section>
      </div>

      {/* Headline CTA */}
      <div className="rounded-2xl border border-cyan-300/20 bg-gradient-to-r from-cyan-500/[0.06] to-violet-500/[0.06] p-8 text-center">
        <p className="text-xl font-bold text-white mb-2">
          FROM CAPITAL PLANNING TO HUMAN-CAPITAL PLANNING
        </p>
        <p className="text-sm text-slate-300 max-w-xl mx-auto">
          The {fmt(ov?.combined_total)} bond builds schools. FuturePath connects those schools to students, pathways, and the community outcomes that make the investment matter.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <DataBadge type="official_public_data" label={`${fmt(ov?.combined_total)} Official Bond Data`} />
          <span className="text-slate-300">+</span>
          <DataBadge type="futurepath_live_data" label={`${(roi?.student_roi?.students_in_district || 0).toLocaleString()} Student Intelligence Profiles`} />
          <span className="text-slate-300">=</span>
          <DataBadge type="futurepath_modeled_scenario" label="Human-Capital Intelligence" />
        </div>
      </div>
    </div>
  );
}

// ─── Presentation Mode ────────────────────────────────────────────────────────
function PresentationMode({ overview: ov, projects, roi, caryDetail, onExit }) {
  const [slide, setSlide] = useState(0);

  const NEW_SCHOOLS = projects.filter(p => p.project_type === 'new_school');
  const RENOVATIONS = projects.filter(p => p.project_type === 'renovation');

  const SLIDES = [
    {
      num: '01', title: 'The Investment',
      subtitle: 'What Wake County is proposing',
      content: (
        <div className="grid gap-6 sm:grid-cols-3 mt-8">
          {[
            { label: 'General Obligation Bond', value: fmt(ov?.go_bond_amount), color: 'text-cyan-300', note: 'Voter referendum' },
            { label: 'Pay-As-You-Go Cash',       value: fmt(ov?.paygo_amount),   color: 'text-emerald-300', note: 'No additional debt' },
            { label: 'Combined Investment',       value: fmt(ov?.combined_total), color: 'text-violet-300', note: 'FY28-FY29 program' },
          ].map(item => (
            <div key={item.label} className="text-center p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03]">
              <p className={`text-5xl font-black ${item.color}`}>{item.value}</p>
              <p className="mt-2 text-sm text-white font-semibold">{item.label}</p>
              <p className="text-[11px] text-slate-300">{item.note}</p>
            </div>
          ))}
        </div>
      ),
      badge: 'official_public_data',
    },
    {
      num: '02', title: 'Where It Goes',
      subtitle: `${projects.length} capital projects across Wake County`,
      content: (
        <div className="grid gap-6 sm:grid-cols-2 mt-8">
          <div className="p-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] text-center">
            <p className="text-6xl font-black text-emerald-300">{NEW_SCHOOLS.length}</p>
            <p className="mt-2 text-sm text-white font-semibold">New Schools</p>
            <div className="mt-3 space-y-1">
              {NEW_SCHOOLS.map(s => (
                <p key={s.school_name} className="text-[11.5px] text-slate-300">{s.school_name}</p>
              ))}
            </div>
          </div>
          <div className="p-6 rounded-2xl border border-violet-500/20 bg-violet-500/[0.05] text-center">
            <p className="text-6xl font-black text-violet-300">{RENOVATIONS.length}</p>
            <p className="mt-2 text-sm text-white font-semibold">Renovations & Replacements</p>
            <div className="mt-3 space-y-1">
              {[...new Set(RENOVATIONS.map(s => s.school_name))].map(n => (
                <p key={n} className="text-[11.5px] text-slate-300">{n}</p>
              ))}
            </div>
          </div>
        </div>
      ),
      badge: 'official_public_data',
    },
    {
      num: '02b', title: 'Spotlight: Cary High',
      subtitle: '$85.6M · FY2029 · The intelligence behind a single project',
      content: (
        <CaryHighSlide caryDetail={caryDetail} />
      ),
      badge: 'futurepath_live_data',
    },
    {
      num: '03', title: 'Who It Serves',
      subtitle: 'The student communities connected to these schools',
      content: (
        <div className="text-center mt-12">
          <p className="text-8xl font-black text-cyan-300">{(roi?.student_roi?.students_in_district || 0).toLocaleString()}</p>
          <p className="mt-4 text-xl text-white">Students in Wake County on FuturePath</p>
          <p className="mt-2 text-sm text-slate-300">with live career plans, pathway data, and academic profiles</p>
        </div>
      ),
      badge: 'futurepath_live_data',
    },
    {
      num: '04', title: 'What It Enables',
      subtitle: 'Programs, capacity, and learning environments',
      content: (
        <div className="grid gap-4 sm:grid-cols-2 mt-8">
          {[
            { label: 'New Seats Created', value: (roi?.student_roi?.capacity_created || 0).toLocaleString(), icon: Users },
            { label: 'CTE Pathways Supported', value: '37 schools', icon: Briefcase },
            { label: 'Technology Investments', value: 'Included', icon: Zap },
            { label: 'Security & Access', value: 'Upgraded', icon: Shield },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-4 p-5 rounded-2xl border border-white/[0.07] bg-white/[0.03]">
              <item.icon className="h-6 w-6 text-cyan-400 shrink-0" />
              <div>
                <p className="text-xl font-bold text-white">{item.value}</p>
                <p className="text-[12px] text-slate-300">{item.label}</p>
              </div>
            </div>
          ))}
        </div>
      ),
      badge: 'official_public_data',
    },
    {
      num: '05', title: 'Where Students Are Going',
      subtitle: 'Live career pathway distribution, Wake County students',
      content: (
        <div className="text-center mt-8">
          <p className="text-slate-300 text-sm">Live pathway data available in FuturePath District Intelligence Hub</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {['Four-Year College', 'Community College', 'CTE / Workforce', 'Military', 'Entrepreneurship'].map(p => (
              <div key={p} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 text-center">
                <p className="text-sm text-slate-300">{p}</p>
              </div>
            ))}
          </div>
        </div>
      ),
      badge: 'futurepath_live_data',
    },
    {
      num: '08', title: 'Scenario Lab',
      subtitle: 'Change one education outcome, see what moves',
      content: (
        <div className="mt-8 space-y-3">
          {[
            { label: 'CTE Participation',         baseline: '38%', example: '+10% → 42%' },
            { label: 'Work-Based Learning',        baseline: '22%', example: '+25% → 28%' },
            { label: 'Credential Attainment',      baseline: '31%', example: '+15% → 36%' },
            { label: 'Postsecondary Persistence',  baseline: '68%', example: '+10% → 75%' },
            { label: 'Local Talent Retention',     baseline: '42%', example: '+10% → 46%' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-4 px-5 py-3 rounded-xl border border-white/[0.07] bg-white/[0.02]">
              <p className="flex-1 text-[13px] text-slate-300">{item.label}</p>
              <p className="text-[12px] text-slate-300">Baseline: {item.baseline}</p>
              <p className="text-[12px] font-semibold text-cyan-300">{item.example}</p>
            </div>
          ))}
        </div>
      ),
      badge: 'futurepath_modeled_scenario',
    },
    {
      num: '09', title: 'Community Impact',
      subtitle: 'Modeled human-capital outcomes',
      content: (
        <div className="text-center mt-8 space-y-4">
          <p className="text-7xl font-black text-violet-300">$4.7B</p>
          <p className="text-xl text-white">Modeled 10-year earnings impact</p>
          <p className="text-sm text-slate-300">10-year cohort of ~2,500 students/yr × improved local retention × NC median earnings</p>
          <div className="mt-4 mx-auto max-w-md rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-4">
            <p className="text-[11px] text-amber-400 font-semibold uppercase tracking-wide mb-1">FUTUREPATH MODELED SCENARIO</p>
            <p className="text-[11px] text-slate-300">These are modeled estimates, not predictions. They represent potential outcomes.</p>
          </div>
        </div>
      ),
      badge: 'futurepath_modeled_scenario',
    },
    {
      num: '10', title: '$832.3M Builds Schools.',
      subtitle: 'What if we could also see the human capital those schools will build?',
      content: (
        <div className="text-center mt-10 space-y-6">
          <p className="text-2xl text-white font-bold">FROM CAPITAL PLANNING TO HUMAN-CAPITAL PLANNING</p>
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            {[fmt(ov?.go_bond_amount), '+', fmt(ov?.paygo_amount), '=', fmt(ov?.combined_total)].map((v, i) => (
              v === '+' || v === '=' ? (
                <p key={i} className="text-3xl font-black text-slate-300 self-center">{v}</p>
              ) : (
                <div key={i} className="text-center px-6 py-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.06]">
                  <p className="text-3xl font-black text-cyan-300">{v}</p>
                </div>
              )
            ))}
          </div>
          <p className="text-xl font-black text-cyan-200 mt-8">Agentic FuturePath AI</p>
          <p className="text-sm text-slate-300">Community Impact Intelligence Platform</p>
        </div>
      ),
      badge: 'brand',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#020b14] flex flex-col" data-testid="presentation-mode">
      {/* Controls */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-4">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-cyan-400">
            Wake County Bond Intelligence
          </span>
          <span className="text-[11px] text-slate-300">Slide {slide + 1} of {SLIDES.length}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSlide(Math.max(0, slide - 1))} disabled={slide === 0}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-[11px] text-slate-300
              hover:bg-white/[0.04] disabled:opacity-30 flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Prev
          </button>
          <button onClick={() => setSlide(Math.min(SLIDES.length - 1, slide + 1))} disabled={slide === SLIDES.length - 1}
            className="rounded-lg border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[11px]
              text-cyan-300 hover:bg-cyan-400/[0.13] disabled:opacity-30 flex items-center gap-1">
            Next <ChevronRight className="h-3 w-3" />
          </button>
          <button onClick={onExit} className="rounded-lg border border-white/10 px-3 py-1.5 text-[11px]
            text-slate-300 hover:text-white hover:bg-white/[0.04] flex items-center gap-1">
            <X className="h-3 w-3" /> Exit
          </button>
        </div>
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-auto px-12 py-10 max-w-5xl mx-auto w-full">
        <AnimatePresence mode="wait">
          <motion.div key={slide}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}>

            <div className="flex items-center gap-3 mb-3">
              <span className="text-[10.5px] font-bold text-slate-300 tracking-[0.2em]">
                SLIDE {SLIDES[slide].num}
              </span>
              <DataBadge
                type={SLIDES[slide].badge}
                label={
                  SLIDES[slide].badge === 'official_public_data' ? 'Verified Public Data' :
                  SLIDES[slide].badge === 'futurepath_live_data' ? 'FuturePath Live Data' :
                  SLIDES[slide].badge === 'brand' ? 'Agentic FuturePath AI' :
                  'FuturePath Modeled Scenario'
                }
              />
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight">
              {SLIDES[slide].title}
            </h2>
            <p className="mt-2 text-base text-slate-300">{SLIDES[slide].subtitle}</p>
            {SLIDES[slide].content}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Slide dots */}
      <div className="flex justify-center gap-2 pb-6">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => setSlide(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === slide ? 'w-6 bg-cyan-400' : 'w-1.5 bg-white/20'
            }`} />
        ))}
      </div>
    </div>
  );
}



// ── Bond Intelligence Intro Frame ─────────────────────────────────────────
function BondIntroFrame({ onDismiss }) {
  const DATA_TYPES = [
    {
      type: 'official_public_data',
      label: 'Official Public Data',
      dot: 'bg-emerald-400',
      border: 'border-emerald-500/25',
      bg: 'bg-emerald-500/[0.07]',
      title: 'WCPSS & State Sources',
      desc: 'Bond amounts, project budgets, referendum dates, sourced directly from official Wake County and NCDPI public records. Never modified or estimated.',
    },
    {
      type: 'futurepath_live_data',
      label: 'FuturePath Live Data',
      dot: 'bg-cyan-400',
      border: 'border-cyan-500/25',
      bg: 'bg-cyan-500/[0.07]',
      title: 'Aggregate Student Trends',
      desc: 'District-level pathway distributions, career interests, and readiness scores, derived from student platform activity. Always school or district totals, never individual records.',
    },
    {
      type: 'futurepath_modeled_scenario',
      label: 'Modeled Scenarios',
      dot: 'bg-violet-400',
      border: 'border-violet-500/25',
      bg: 'bg-violet-500/[0.07]',
      title: 'What-If Projections',
      desc: 'Scenario Lab outcomes and ROI estimates, clearly labeled projections based on input levers. Clearly distinguished from measured data at every step.',
    },
  ];

  return (
    <div className="rounded-2xl border border-cyan-400/15 bg-[#0b1b30] p-6"
      data-testid="bond-intro-frame">

      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Info className="h-4 w-4 text-cyan-400 shrink-0" />
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              About This Intelligence Tool
            </span>
          </div>
          <p className="text-[13px] text-slate-300 max-w-2xl leading-relaxed">
            This platform connects <strong className="text-white">public capital investment</strong> (the
            $832.3M bond program) to <strong className="text-white">human-capital outcomes</strong>, student
            pathway trends, career readiness, and community workforce direction. It is an{' '}
            <strong className="text-white">outcomes planning tool</strong>, not individual student surveillance.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="shrink-0 rounded-lg border border-white/10 bg-white/[0.03] p-1.5
            text-slate-300 hover:text-white hover:bg-white/[0.07] transition-colors"
          data-testid="dismiss-bond-intro"
          aria-label="Dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Three data type cards */}
      <div className="grid gap-3 sm:grid-cols-3">
        {DATA_TYPES.map(dt => (
          <div key={dt.type}
            className={`rounded-xl border ${dt.border} ${dt.bg} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`h-2 w-2 rounded-full shrink-0 ${dt.dot}`} />
              <span className="text-[10.5px] font-semibold text-slate-200">{dt.label}</span>
            </div>
            <p className="text-[11.5px] font-semibold text-white mb-1">{dt.title}</p>
            <p className="text-[11px] text-slate-300 leading-relaxed">{dt.desc}</p>
          </div>
        ))}
      </div>

      {/* Clarity statement */}
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-white/[0.06]
        bg-white/[0.02] px-4 py-3">
        <Shield className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-[11.5px] text-slate-300 leading-relaxed">
          <strong className="text-emerald-300">Data separation is enforced by design.</strong>{' '}
          Individual student records are never exposed at the district capital level.
          Every number shown is either a verified public figure or a clearly labeled aggregate or projection.
          FERPA compliance is maintained at all times.
        </p>
      </div>
    </div>
  );
}